import { MarketLevel } from '@ps/isp-web-services-types';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import React, { useEffect, useState } from 'react';

import getWindow from '@swf/core/utils/getWindow';
import type { DateTimeOptions } from '@swf/sports/components/FormattedDateTime/FormattedDateTime';

import EventStatusMessage from '../../components/EventStatusMessage/EventStatusMessage';
import EventStatusTime from '../../components/EventStatusTime/EventStatusTime';
import type { EventStatus } from '../../hooks/useEventStatus';
import useEventStatus from '../../hooks/useEventStatus';
import type { EventStatusSize } from '../../types';

const secureWindow = getWindow();
const SOON_IN_PLAY_INTERVAL = 1000;

const getMinutesUntilStart = (startTime: string) =>
  differenceInMinutes(new Date(startTime), new Date(), {
    roundingMethod: 'ceil'
  });

export interface Props {
  isInPlay?: boolean;
  isTurnInPlay?: boolean;
  startTime: string;
  timeFormat?: DateTimeOptions['format'];
  size?: EventStatusSize;
  eventStatusOverride?: EventStatus;
  marketLevels?: MarketLevel[];
}

const DefaultEventStatus: React.FC<Props> = ({
  timeFormat = 'time',
  size = 'XSmall',
  isInPlay,
  isTurnInPlay,
  ...props
}) => {
  const eventStatusProps = {
    ...props,
    inPlay: isInPlay,
    canTurnInPlay: isTurnInPlay
  };
  const eventStatus = useEventStatus(eventStatusProps);
  const [minsUntilStart, setMinsUntilStart] = useState(getMinutesUntilStart(props.startTime));

  useEffect(() => {
    let interval: number | undefined;

    if (eventStatus === 'SOON_IN_PLAY') {
      setMinsUntilStart(getMinutesUntilStart(props.startTime));
      interval = secureWindow?.setInterval(
        () => setMinsUntilStart(getMinutesUntilStart(props.startTime)),
        SOON_IN_PLAY_INTERVAL
      );
    }

    return () => {
      secureWindow?.clearInterval(interval);
    };
  }, [eventStatus, props.startTime]);

  if (props.marketLevels && !props.marketLevels.includes(MarketLevel.AVB_EVENT)) {
    return null;
  }

  switch (eventStatus) {
    case 'IN_PLAY':
      return <EventStatusMessage messageId="IN_PLAY" iconName="in-play" size={size} />;
    case 'GOING_IN_PLAY':
      return <EventStatusMessage messageId="GOING_IN_PLAY" size={size} />;
    case 'SOON_IN_PLAY': {
      return (
        <EventStatusMessage
          messageId="STARTS_IN_X_MINS"
          size={size}
          values={{ minutes: minsUntilStart }}
        />
      );
    }
    case 'COMPLETE':
      return <EventStatusMessage messageId="COMPLETE" size={size} />;
    default:
      return <EventStatusTime startTime={props.startTime} size={size} timeFormat={timeFormat} />;
  }
};

export default DefaultEventStatus;
